import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl } from "react-intl";
import { UTCToLocalString } from '@/util/dateUtil';
import { usePaginatedSimpleUserSentences } from "@/query/statistics";
import { useAgentId, usePageParam } from "@/hook";
import SortButton from "@/component/SortBurtton";
import { DateRange } from "@/component/dateRangePicker";
import { InView } from 'react-intersection-observer';


type SortKey = "created_at" | "skill_name" | "intent_name" | "query" | "score";

interface UserSentencesTableProps {
    period: DateRange;
}

interface UserSentencesTablePageParam {
    page: number;
    sortKey: SortKey;
    sortOrder: "asc" | "desc";
}

const UserSentencesTable: React.FC<UserSentencesTableProps> = ({ period }) => {
    const intl = useIntl();
    const agentId = useAgentId();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { param, set: setParam, updateCallback } = usePageParam<UserSentencesTablePageParam>({
        page: 0,
        sortKey: "created_at",
        sortOrder: "desc",
    });

    const {
        data: sentencePages,
        hasNextPage,
        fetchNextPage,
    } = usePaginatedSimpleUserSentences(
        agentId,
        period.start?.format("YYYY-MM-DD HH:mm:ss"),
        period.end?.format("YYYY-MM-DD HH:mm:ss"),
        param?.sortKey,
        param?.sortOrder,
    );

    const sentences = useMemo(() => {
        return sentencePages?.pages.map(page => page.content).flat() || [];
    }, [sentencePages]);

    const onScrollToBottom = () => {
        if (hasNextPage) {
            fetchNextPage().then();
        }
    };

    const onClickSort = (key: SortKey) => {
        if (param?.sortKey === key) {
            setParam({
                sortOrder: param?.sortOrder === "asc" ? "desc" : "asc"
            });
        } else {
            setParam({
                sortKey: key
            });
        }
    };

    useEffect(() => {    
        const scrollToTop = () => {
            scrollContainerRef.current?.scrollTo({
                top: 0,
            });
        };
        updateCallback(scrollToTop);
    }, [updateCallback]);

    return (
        <div role="table"
             className="selection-table grid-table-user-sentences"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th group pl-[25px]"
                >
                    <SortButton order={param?.sortOrder}
                                active={param?.sortKey === "created_at"}    
                                onClick={() => onClickSort("created_at")}
                    >
                        {intl.formatMessage({ id: "i02012" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[15px]"
                >
                    <SortButton order={param?.sortOrder}
                                active={param?.sortKey === "query"}    
                                onClick={() => onClickSort("query")}
                    >
                        {intl.formatMessage({ id: "i02015" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[15px]"
                >
                    <SortButton order={param?.sortOrder}
                                active={param?.sortKey === "skill_name"}    
                                onClick={() => onClickSort("skill_name")}
                    >
                        {intl.formatMessage({ id: "i02013" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[15px]"
                >
                    <SortButton order={param?.sortOrder}
                                active={param?.sortKey === "intent_name"}    
                                onClick={() => onClickSort("intent_name")}
                    >
                        {intl.formatMessage({ id: "i02014" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[15px]"
                >
                    <SortButton order={param?.sortOrder}
                                active={param?.sortKey === "score"}    
                                onClick={() => onClickSort("score")}
                    >
                        {intl.formatMessage({ id: "i02021" })}
                    </SortButton>
                </div>
            </div>
            <div ref={scrollContainerRef}
                 role="rowgroup"
                 className="selection-table-tbody max-h-[702px]"
            >
                {sentences?.map((sentence, index) => (
                    <div key={index}
                         role="row"
                         className="selection-table-tr"
                    >
                        <div role="cell"
                             className="selection-table-td px-[25px]"
                        >
                            {UTCToLocalString(sentence.created_at, "YYYY-MM-DD HH:mm:ss")}
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[15px]"
                        >
                            <span className="long-text-wrapper">
                                {sentence.query}
                            </span>
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[15px]"
                        >
                            {sentence.skill_name || "-"}
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[15px]"
                        >
                            {sentence.intent_name || "-"}
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[15px]"
                        >
                            {sentence.score || "-"}
                        </div>
                    </div>
                ))}
                {hasNextPage && (
                    <InView as="div"
                            className="h-px"
                            onChange={inView => inView && onScrollToBottom()}
                    />
                )}
                {!sentences?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: "i02016" })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserSentencesTable;
